import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, LogOut, User, ChevronDown, Moon, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/auth.store';
import { cn } from '@/lib/utils';
import { notificationsService } from '@/services/notifications.service';

const THEME_KEY = 'at360_theme';

function getInitialTheme(): boolean {
  const stored = localStorage.getItem(THEME_KEY);
  if (stored !== null) return stored === 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function Header() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, clearUser } = useAuthStore();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [darkMode, setDarkMode] = useState(getInitialTheme);

  const { data: unread } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => notificationsService.unreadCount(),
    refetchInterval: 60_000,
    enabled: !!user,
  });
  const unreadCount = unread?.count ?? 0;

  const { data: latestPage } = useQuery({
    queryKey: ['notifications', 'latest'],
    queryFn: () => notificationsService.list({ limit: 5 }),
    enabled: showNotifications,
  });
  const latest = latestPage?.data ?? [];

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem(THEME_KEY, darkMode ? 'dark' : 'light');
  }, [darkMode]);

  const handleLogout = useCallback(() => {
    clearUser();
    localStorage.removeItem('at360_access_token');
    localStorage.removeItem('at360_refresh_token');
    navigate('/login');
  }, [clearUser, navigate]);

  const toggleDarkMode = () => setDarkMode((prev) => !prev);

  return (
    <header
      className="flex h-16 items-center justify-between border-b bg-card px-6"
      role="banner"
    >
      {/* Breadcrumb area */}
      <div className="flex items-center gap-2">
        <h1 className="text-lg font-semibold sr-only">Asset Track 360</h1>
      </div>

      {/* Right side actions */}
      <div className="flex items-center gap-2">
        {/* Dark mode toggle */}
        <button
          type="button"
          onClick={toggleDarkMode}
          className="inline-flex h-11 w-11 items-center justify-center rounded-lg hover:bg-accent transition-colors"
          aria-label={darkMode ? t('header.switchToLight') : t('header.switchToDark')}
        >
          {darkMode ? (
            <Sun className="h-5 w-5" aria-hidden="true" />
          ) : (
            <Moon className="h-5 w-5" aria-hidden="true" />
          )}
        </button>

        {/* Notifications */}
        <div className="relative">
          <button
            type="button"
            onClick={() => {
              setShowNotifications(!showNotifications);
              setShowUserMenu(false);
            }}
            className="relative inline-flex h-11 w-11 items-center justify-center rounded-lg hover:bg-accent transition-colors"
            aria-label={t('header.notifications')}
            aria-haspopup="true"
            aria-expanded={showNotifications}
            data-testid="notifications-bell"
          >
            <Bell className="h-5 w-5" aria-hidden="true" />
            {unreadCount > 0 && (
              <span
                className="absolute right-1 top-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold leading-none text-white"
                data-testid="notifications-unread-count"
                aria-label={`${unreadCount} notificaciones sin leer`}
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>
          {showNotifications && (
            <div
              className="absolute right-0 top-full mt-2 w-96 rounded-xl border bg-card p-4 shadow-lg animate-scale-in"
              role="menu"
            >
              <div className="mb-3 flex items-center justify-between">
                <h3 className="font-semibold text-sm">{t('header.notifications')}</h3>
                <button
                  type="button"
                  onClick={() => {
                    navigate('/notificaciones');
                    setShowNotifications(false);
                  }}
                  className="text-xs font-medium text-primary hover:underline"
                >
                  Ver todas
                </button>
              </div>
              {latest.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  {t('header.noNotifications')}
                </div>
              ) : (
                <ul className="max-h-80 space-y-2 overflow-y-auto" role="list">
                  {latest.map((n) => (
                    <li
                      key={n.id}
                      className={`rounded-md border p-3 text-sm ${
                        n.priority === 'HIGH'
                          ? 'border-red-300 bg-red-50'
                          : n.priority === 'MEDIUM'
                          ? 'border-amber-300 bg-amber-50'
                          : 'border-blue-300 bg-blue-50'
                      } ${n.readAt ? 'opacity-60' : ''}`}
                    >
                      <p className="font-semibold">{n.title}</p>
                      <p className="text-xs text-muted-foreground">{n.message}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* User menu */}
        <div className="relative">
          <button
            type="button"
            onClick={() => {
              setShowUserMenu(!showUserMenu);
              setShowNotifications(false);
            }}
            className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-accent transition-colors min-h-[44px]"
            aria-label={t('header.userMenu')}
            aria-haspopup="true"
            aria-expanded={showUserMenu}
          >
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
              {user?.firstName?.[0]}
              {user?.lastName?.[0]}
            </div>
            <div className="hidden text-left md:block">
              <p className="text-sm font-medium">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-xs text-muted-foreground">
                {user?.role ? t(`enums.rolesLong.${user.role}`, user.role) : ''}
              </p>
            </div>
            <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          </button>
          {showUserMenu && (
            <div
              className="absolute right-0 top-full z-50 mt-2 w-56 rounded-xl border bg-card py-1 shadow-lg animate-scale-in"
              role="menu"
            >
              <div className="border-b px-4 py-3 md:hidden">
                <p className="font-medium">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
              </div>
              <button
                type="button"
                className="flex w-full items-center gap-2 px-4 py-2.5 text-sm hover:bg-accent transition-colors min-h-[44px]"
                role="menuitem"
                onClick={() => {
                  navigate('/perfil');
                  setShowUserMenu(false);
                }}
              >
                <User className="h-4 w-4" aria-hidden="true" />
                {t('nav.profile')}
              </button>
              <div className="border-t" />
              <button
                type="button"
                className={cn(
                  'flex w-full items-center gap-2 px-4 py-2.5 text-sm text-destructive hover:bg-accent transition-colors min-h-[44px]'
                )}
                role="menuitem"
                onClick={handleLogout}
              >
                <LogOut className="h-4 w-4" aria-hidden="true" />
                {t('nav.logout')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Click outside to close menus */}
      {(showUserMenu || showNotifications) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setShowUserMenu(false);
            setShowNotifications(false);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setShowUserMenu(false);
              setShowNotifications(false);
            }
          }}
          role="presentation"
          aria-hidden="true"
        />
      )}
    </header>
  );
}
